// VRG-703 — the paging rules the shared `Table` follows, kept pure so a guard can pin them
// (test/table-pagination.guard.spec.ts). Plain TS on purpose: no React, no MUI import, so
// node's test runner can load it type-stripped.

export type TablePaginationModel = { page: number; pageSize: number }

/**
 * Who owns the page.
 *
 * MUI's DataGrid treats `paginationModel` as CONTROLLED the moment it is passed: every click
 * on page 2 calls `onPaginationModelChange` and then renders whatever `paginationModel` says.
 * A caller that passes `paginationModel={{ page: 0, pageSize: 25 }}` and no handler therefore
 * pins the grid to page 0 for good: the footer reads "1 - 25 of 30" and the buttons do
 * nothing. Eleven internal tables were written that way, each meaning "start at page 0 with
 * N rows", which is an INITIAL value, not a controlled one.
 *
 * So a model with no handler is read as exactly that: the grid's initial state, after which
 * the grid owns paging itself. A caller that wants to own the page passes both halves. A
 * caller that only wants a different page size says so with `initialPageSize`, which is what
 * those eleven tables now pass (the guard refuses the ambiguous literal).
 */
export const resolvePagination = (
  paginationModel: TablePaginationModel | undefined,
  onPaginationModelChange: ((model: TablePaginationModel) => void) | undefined,
  initialPageSize?: number,
): { controlled?: TablePaginationModel; initial?: TablePaginationModel } => {
  if (onPaginationModelChange) return { controlled: paginationModel }
  if (paginationModel) return { initial: paginationModel }
  return initialPageSize ? { initial: { page: 0, pageSize: initialPageSize } } : {}
}

/**
 * The footer's "from - to of total". `page` is the grid's 0-based page; `pageSize` -1 is
 * "All"; `total` -1 is an unknown server count.
 */
export const formatPageRange = (page: number, pageSize: number, total: number): string => {
  if (total === 0) return '0 - 0 of 0'
  if (pageSize === -1) return `1 - ${total === -1 ? 'all' : total} of ${total === -1 ? 'all' : total}`
  const from = page * pageSize + 1
  const end = (page + 1) * pageSize
  const to = total === -1 ? end : Math.min(total, end)
  return `${from} - ${to} of ${total === -1 ? `more than ${to}` : total}`
}

/**
 * The grid counts pages from 0 and MUI's `Pagination` counts them from 1. The footer used to
 * hand the 0-based page straight across, so page 1 showed no highlighted button and page 2
 * highlighted "1".
 */
export const toPaginationButtonPage = (gridPage: number): number => gridPage + 1
export const toGridPage = (buttonPage: number): number => buttonPage - 1
